import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Menu,
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Fade,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LanguageIcon from '@mui/icons-material/Language';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ReactComponent as Logo } from '../assets/images/logo.svg';
import { styled, keyframes } from '@mui/material/styles';
import { RESONANCE_URL } from '../config/links';

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StyledAppBar = styled(AppBar)(({ scrolled }) => ({
  background: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
  boxShadow: scrolled ? '0 4px 30px rgba(0, 0, 0, 0.1)' : 'none',
  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  backdropFilter: scrolled ? 'blur(10px)' : 'none',
  borderBottom: scrolled ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(12, 132, 102, 0.2), transparent)',
    backgroundSize: '200% 100%',
    animation: `${shimmer} 4s linear infinite`,
    opacity: scrolled ? 1 : 0,
  },
}));

const NavButton = styled(Button)(({ active }) => ({
  position: 'relative',
  overflow: 'hidden',
  padding: '8px 18px',
  textTransform: 'none',
  fontSize: '0.95rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 4,
    left: '50%',
    width: active === 'true' ? '60%' : '0%',
    height: '2px',
    background: '#0C8466',
    transition: 'all 0.3s ease',
    transform: 'translateX(-50%)',
  },
  '&:hover': {
    color: '#0C8466',
    '&::after': {
      width: '60%',
    },
  },
}));

const TryResonanceButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#0C8466',
  color: 'white',
  padding: '10px 24px',
  borderRadius: '4px',
  transition: 'all 0.3s ease',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.95rem',
  letterSpacing: '0.3px',
  boxShadow: '0 4px 14px rgba(12, 132, 102, 0.25)',
  '&:hover': {
    transform: 'translateY(-2px)',
    backgroundColor: '#0a6f55',
    boxShadow: '0 8px 24px rgba(12, 132, 102, 0.4)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 20px',
    fontSize: '0.9rem',
  },
}));

const LogoWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
  },
});

const MobileNavItem = styled(ListItem)(({ theme }) => ({
  borderBottom: '1px solid rgba(12, 132, 102, 0.1)',
  transition: 'all 0.3s ease',
  padding: theme.spacing(2, 3),
  '&:hover': {
    backgroundColor: 'rgba(12, 132, 102, 0.05)',
    paddingLeft: theme.spacing(4),
  },
}));

const StyledMenuItem = styled('div')(({ theme }) => ({
  padding: '10px 20px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(12, 132, 102, 0.1)',
    '& .MuiTypography-root': {
      color: '#0C8466',
    },
  },
}));

const NAV_ITEMS = [
  { key: 'resonance', section: 'resonance' },
  { key: 'research', section: 'biochar' },
  { key: 'advisory', section: 'advisory' },
  { key: 'news', section: 'news' },
  { key: 'about', section: 'about' },
  { key: 'contact', section: 'contact' },
];

const LandingTopBar = ({ scrolled, scrollToSection }) => {
  const { t, i18n } = useTranslation();
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [navVisible, setNavVisible] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleScroll = () => {
      for (const item of NAV_ITEMS) {
        const el = document.getElementById(`${item.section}-section`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(item.section);
            return;
          }
        }
      }
      setActiveSection('');
    };
    window.addEventListener('scroll', handleScroll);
    const timer = setTimeout(() => setNavVisible(true), 300);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const handleLangMenuOpen = (e) => setLangAnchorEl(e.currentTarget);
  const handleLangMenuClose = () => setLangAnchorEl(null);
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    handleLangMenuClose();
  };

  const handleMobileNavClick = (section) => {
    scrollToSection(section);
    setMobileMenuOpen(false);
  };

  const openResonance = () => {
    window.open(RESONANCE_URL, '_blank', 'noopener,noreferrer');
  };

  const textColor = scrolled ? '#1A1A1A' : '#FFFFFF';

  return (
    <>
      <StyledAppBar position="fixed" scrolled={scrolled ? 1 : 0}>
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            minHeight: { xs: 70, md: 80 },
            px: { xs: 2, md: 6 },
          }}
        >
          <LogoWrapper>
            <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <Logo style={{ height: '40px', width: 'auto' }} />
            </Link>
          </LogoWrapper>

          {!isMobile && (
            <Fade in={navVisible} timeout={600}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {NAV_ITEMS.map((item, index) => (
                  <NavButton
                    key={item.section}
                    onClick={() => scrollToSection(item.section)}
                    active={(activeSection === item.section).toString()}
                    sx={{
                      color: textColor,
                      animation: `${fadeIn} 0.5s ease forwards ${0.1 + index * 0.05}s`,
                      opacity: 0,
                    }}
                  >
                    {t(`landing.nav.${item.key}`)}
                  </NavButton>
                ))}
              </Box>
            </Fade>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
            <IconButton
              onClick={handleLangMenuOpen}
              sx={{ color: textColor }}
              aria-label="Change language"
            >
              <LanguageIcon />
            </IconButton>
            <Menu
              anchorEl={langAnchorEl}
              open={Boolean(langAnchorEl)}
              onClose={handleLangMenuClose}
              PaperProps={{
                sx: {
                  mt: 2,
                  minWidth: 120,
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <StyledMenuItem onClick={() => changeLanguage('en')}>
                <Typography variant="body2">English</Typography>
              </StyledMenuItem>
              <StyledMenuItem onClick={() => changeLanguage('ja')}>
                <Typography variant="body2">日本語</Typography>
              </StyledMenuItem>
            </Menu>

            {!isMobile && (
              <TryResonanceButton
                onClick={openResonance}
                endIcon={<ArrowForwardIcon />}
              >
                {t('landing.nav.tryResonance')}
              </TryResonanceButton>
            )}

            {isMobile && (
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{ color: textColor }}
                aria-label="Open menu"
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </StyledAppBar>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: '82%',
            maxWidth: '320px',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            pt: 2,
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ pt: 1 }}>
          {NAV_ITEMS.map((item) => (
            <MobileNavItem
              key={item.section}
              button
              onClick={() => handleMobileNavClick(item.section)}
            >
              <ListItemText
                primary={t(`landing.nav.${item.key}`)}
                primaryTypographyProps={{
                  fontWeight: activeSection === item.section ? 600 : 400,
                  color: activeSection === item.section ? '#0C8466' : 'inherit',
                }}
              />
            </MobileNavItem>
          ))}
          <Box sx={{ p: 3 }}>
            <TryResonanceButton
              fullWidth
              onClick={() => { openResonance(); setMobileMenuOpen(false); }}
              endIcon={<ArrowForwardIcon />}
            >
              {t('landing.nav.tryResonance')}
            </TryResonanceButton>
          </Box>
        </List>
      </Drawer>
    </>
  );
};

export default LandingTopBar;
